import { useCallback, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { NBXStorage } from '../storage';
import { Session } from '../types';
// eslint-disable-next-line import/no-cycle
import {
  readDeviceToken,
  isDeviceTokenExists,
  getLastBioAuthenticatedEpochMs,
  refreshSession,
  refreshUserToken,
  refreshAccountToken
} from '.';
import { useLastBioAuthenticatedEpochMs } from './deviceToken';
import { useAuthAccountClaims, useAuthSession } from '../subscribe';
import { publishSessionExpired } from './session';

const TIMEOUT_MS = 5 * 60 * 1000;

const isTimeout = async () => {
  const lastAuthDate = await getLastBioAuthenticatedEpochMs();
  return !lastAuthDate || Date.now() - +lastAuthDate > TIMEOUT_MS;
};

const isBiometricAvailable = async () => Capacitor.isNativePlatform() && (await isDeviceTokenExists());

interface UseBiometricGateProps {
  showWhenNoSession: boolean;
  onSuccess?: (session: Session) => void;
  tag?: string;
}

export const useBiometricGate = (
  { showWhenNoSession = false, onSuccess, tag }: UseBiometricGateProps = { showWhenNoSession: false }
) => {
  const log = (...args: unknown[]): void => console.log(`useBiometricGate: [${tag}]`, ...args);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const authSession = useAuthSession();
  const lastBioAuthenticatedEpochMs = useLastBioAuthenticatedEpochMs();

  const accountClaims = useAuthAccountClaims();
  const isTestAccount = accountClaims?.roles?.includes('test');

  const [show, setShow] = useState<boolean>(false);
  const [ask, setAsk] = useState<boolean>(false);

  const refreshTokens = useCallback(async (session?: Session | null) => {
    if (!session) {
      log(`cannot refreshTokens - no session`);
      return;
    }
    try {
      setLoading(true);
      const activeAccountId = await NBXStorage.getItem('active-account');
      log(
        `refreshAccountToken(session.exp=${session?.exp}, session.accountId=${
          activeAccountId ?? session?.accountId
        })`
      );
      await refreshAccountToken(session, activeAccountId ?? session.accountId);
      log(`refreshUserToken(session.exp=${session?.exp})`);
      await refreshUserToken(session);
      log(`refresh access tokens - DONE`);
      setAsk(true);
    } catch (e) {
      console.error(
        `useBiometricGate: cannot refresh tokens ${e instanceof Error ? e.message : 'unknown error'}`,
        e
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show && authSession) {
      log(`refreshTokens(authSession.exp=${authSession?.exp})`);
      refreshTokens(authSession);
    } else if (show && authSession === null) {
      publishSessionExpired();
    }
  }, [show, authSession]);

  const authenticate = useCallback(async () => {
    try {
      setError(false);
      setLoading(true);
      log(`readDeviceToken`);
      const deviceTokenJSON = await readDeviceToken();
      log(
        `readDeviceToken - refreshSession from (deviceTokenJSON=${!!deviceTokenJSON}), (isTestAccount=${isTestAccount})`
      );
      const session = await refreshSession(null, deviceTokenJSON, isTestAccount);
      log(`readDeviceToken - refreshSession done (new session.exp=${session?.exp})`);
      onSuccess && onSuccess(session);
      setShow(false);
    } catch (e) {
      console.error(
        `useBiometricGate: authenticate error ${e instanceof Error ? e.message : 'unknown error'}`,
        e
      );
      setError(true);
    } finally {
      log(`readDeviceToken - DONE`);
      setAsk(false);
      setLoading(false);
    }
  }, [isTestAccount]);

  useEffect(() => {
    (async () =>
      (!!showWhenNoSession || !!authSession) && (await isBiometricAvailable()) && (await isTimeout()))().then(
      setShow
    );
  }, [lastBioAuthenticatedEpochMs, showWhenNoSession, authSession]);

  const updateShow = useCallback(async () => {
    const show =
      (!!showWhenNoSession || !!(await NBXStorage.getItem('session'))) &&
      (await isBiometricAvailable()) &&
      (await isTimeout());
    log(`applicationDidBecomeActive updateShow (show=${show})`);
    setShow(show);
  }, [showWhenNoSession]);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      window.addEventListener('applicationDidBecomeActive', updateShow);
      return () => {
        window.removeEventListener('applicationDidBecomeActive', updateShow);
      };
    }
  }, [updateShow]);

  return { show, ask, authenticate, refreshTokens, loading, error };
};
